import { create } from "zustand";
import { persist } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";
import { useCartStore } from "./CartStore";

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      loading: true,

      // ✅ Initialize user from token
      initializeAuth: async () => {
        const storedToken = localStorage.getItem("token");

        try {
          if (storedToken) {
            const decodedUser = jwtDecode(storedToken);
            const currentTime = Math.floor(Date.now() / 1000);

            if (decodedUser.exp < currentTime) {
              // Token expired
              localStorage.removeItem("token");
              set({ user: null });
            } else {
              set({ user: decodedUser });
              // ✅ Fetch cart after restoring user
              const cartStore = useCartStore.getState();
              await cartStore.fetchCart();
              await cartStore.fetchCartCount();
            }
          } else {
            set({ user: null });
          }
        } catch (err) {
          localStorage.removeItem("token");
          set({ user: null });
        } finally {
          set({ loading: false });
        }
      },

      // ✅ Login: set token, decode user, and load cart
      login: async (userToken) => {
        localStorage.setItem("token", userToken);
        const decodedUser = jwtDecode(userToken);
        set({ user: decodedUser, loading: false });

        // Fetch cart immediately after login
        const cartStore = useCartStore.getState();
        await cartStore.fetchCart();
        await cartStore.fetchCartCount();
      },

      // ✅ Logout: clear token and both cart + storage
      logout: async () => {
        // Remove tokens
        localStorage.removeItem("token");
        sessionStorage.clear();

        // Clear cart both locally and from backend
        const cartStore = useCartStore.getState();

        // Clear persisted cart store manually
        localStorage.removeItem("cart-storage");

        // Reset both stores
        cartStore.cartItems = [];
        cartStore.totalItems = 0;

        set({ user: null, loading: false });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({ user: state.user }), // only persist user
    }
  )
);

export default useAuthStore;
